import { useEffect } from 'react';
import {
  Image,
  type ImageSourcePropType,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { Link, type Href, usePathname, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { WebHead } from '../WebHead';
import type { ArticleSource, ArticleSummary } from '../../lib/content/articles';
import { ARTICLE_SUMMARIES } from '../../lib/content/articles';
import {
  trackMarketingCtaClicked,
  trackMarketingPageViewed,
} from '../../lib/product-events';
import { setPendingMarketingAttribution } from '../../lib/marketing-attribution';

function getSiteOrigin(): string {
  if (typeof globalThis.location?.origin === 'string') {
    return globalThis.location.origin;
  }

  return process.env['EXPO_PUBLIC_SITE_URL'] ?? 'https://perdiem.app';
}

type ArticleLayoutProps = {
  article: ArticleSummary;
  children: React.ReactNode;
  relatedSlugs?: string[];
};

export function ArticleLayout({
  article,
  children,
  relatedSlugs = [],
}: ArticleLayoutProps) {
  const router = useRouter();
  const pathname = usePathname();
  const canonicalUrl = `${getSiteOrigin()}${article.path}`;
  const isPhoneHero = article.heroImageFrame === 'phone';
  const relatedArticles = ARTICLE_SUMMARIES.filter((item) =>
    relatedSlugs.includes(item.slug),
  );

  useEffect(() => {
    trackMarketingPageViewed({
      route: pathname,
      pageType: article.pageType,
      pageSlug: article.slug,
      articleSlug: article.slug,
      title: article.title,
    });
  }, [article.pageType, article.slug, article.title, pathname]);

  const handleSignupCta = (placement: string) => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: article.pageType,
      pageSlug: article.slug,
      articleSlug: article.slug,
      ctaId: 'create-account',
      destination: 'signup',
      placement,
    });

    void setPendingMarketingAttribution({
      source: 'marketing_article',
      pageType: article.pageType,
      pageSlug: article.slug,
      articleSlug: article.slug,
      ctaId: 'create-account',
      destination: 'signup',
      placement,
    });

    router.push('/(auth)/login');
  };

  const handleDemoCta = (placement: string) => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: article.pageType,
      pageSlug: article.slug,
      articleSlug: article.slug,
      ctaId: 'try-demo-profiles',
      destination: 'demo_profiles',
      placement,
    });

    void setPendingMarketingAttribution({
      source: 'marketing_article',
      pageType: article.pageType,
      pageSlug: article.slug,
      articleSlug: article.slug,
      ctaId: 'try-demo-profiles',
      destination: 'demo_profiles',
      placement,
    });

    router.push('/?demo=1');
  };

  if (Platform.OS !== 'web') {
    return null;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.seoDescription,
    author: {
      '@type': 'Person',
      name: 'Sam',
      jobTitle: 'Founder',
      worksFor: {
        '@type': 'Organization',
        name: 'Per Diem',
        url: getSiteOrigin(),
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'Per Diem',
      url: getSiteOrigin(),
    },
    datePublished: article.updatedIso,
    dateModified: article.updatedIso,
    mainEntityOfPage: canonicalUrl,
  };

  return (
    <>
      <WebHead>
        <title>{article.seoTitle}</title>
        <meta name="description" content={article.seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      </WebHead>

      <ScrollView className="flex-1 bg-stone-50" contentContainerStyle={{ paddingBottom: 64 }}>
        <View className="border-b border-stone-200 bg-white">
          <View className="mx-auto w-full max-w-6xl gap-4 px-5 py-5 md:flex-row md:items-center md:justify-between md:px-6">
            <Link href="/" asChild>
              <Pressable className="gap-1">
                <Text className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700">
                  Per Diem
                </Text>
                <Text className="text-sm text-stone-500">Daily spending. Lifetime freedom.</Text>
              </Pressable>
            </Link>

            <View className="w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
              <Pressable
                onPress={() => handleDemoCta('nav')}
                className="w-full items-center rounded-full border border-stone-300 px-4 py-2 md:w-auto"
              >
                <Text className="text-sm font-semibold text-stone-700">Try the demo profiles</Text>
              </Pressable>
              <Pressable
                onPress={() => handleSignupCta('nav')}
                className="w-full items-center rounded-full bg-emerald-600 px-4 py-2 md:w-auto"
              >
                <Text className="text-sm font-semibold text-white">Create your account</Text>
              </Pressable>
            </View>
          </View>
        </View>

        <View className="mx-auto w-full max-w-5xl px-6 pt-10">
          <View className="mb-8 max-w-3xl">
            <Text className="mb-3 self-start rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">
              {article.categoryLabel}
            </Text>
            <Text className="text-4xl font-semibold leading-[46px] text-stone-950 md:text-5xl md:leading-[56px]">
              {article.title}
            </Text>
            <Text className="mt-5 text-lg leading-8 text-stone-600">
              {article.description}
            </Text>
            <View className="mt-6 flex-row flex-wrap items-center gap-3">
              <Text className="text-sm font-medium text-stone-700">
                By Sam, founder of Per Diem
              </Text>
              <Text className="text-sm text-stone-400">•</Text>
              <Text className="text-sm text-stone-500">Updated {article.publishedLabel}</Text>
            </View>
          </View>

          <View
            className="overflow-hidden rounded-[28px] border border-stone-200 bg-white shadow-sm"
            style={isPhoneHero ? { alignSelf: 'center', maxWidth: 390, width: '100%' } : undefined}
          >
            <Image
              source={article.heroImage}
              accessibilityLabel={article.heroImageAlt}
              style={{ width: '100%', height: isPhoneHero ? 620 : 460 }}
              resizeMode={isPhoneHero ? 'contain' : 'cover'}
            />
          </View>

          <ArticleCtaBlock
            title="See how Per Diem works"
            description="Start with the demo profiles if you want to explore the product first, or create an account if you're ready to connect your own finances."
            onTryDemo={() => handleDemoCta('post-hero')}
            onSignup={() => handleSignupCta('post-hero')}
          />

          <View className="mx-auto mt-10 w-full max-w-3xl gap-8">{children}</View>

          <ArticleDisclaimer />

          <ArticleCtaBlock
            title="Want to see if Per Diem fits your household?"
            description="The lowest-friction path is to open the demo profiles first. If it clicks, create an account and try it with your own data."
            onTryDemo={() => handleDemoCta('footer')}
            onSignup={() => handleSignupCta('footer')}
          />

          {relatedArticles.length > 0 && (
            <View className="mx-auto mt-12 w-full max-w-4xl">
              <Text className="mb-4 text-2xl font-semibold text-stone-950">
                Related articles
              </Text>
              <View className="gap-4 md:flex-row">
                {relatedArticles.map((item) => (
                  <Link key={item.slug} href={item.path as Href} asChild>
                    <Pressable className="flex-1 rounded-[24px] border border-stone-200 bg-white p-5 shadow-sm">
                      <Text className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">
                        {item.categoryLabel}
                      </Text>
                      <Text className="mt-3 text-xl font-semibold text-stone-950">
                        {item.title}
                      </Text>
                      <Text className="mt-3 text-sm leading-6 text-stone-600">
                        {item.excerpt}
                      </Text>
                      <View className="mt-4 flex-row items-center gap-2">
                        <Text className="text-sm font-medium text-emerald-700">Read article</Text>
                        <Ionicons name="arrow-forward" size={14} color="#047857" />
                      </View>
                    </Pressable>
                  </Link>
                ))}
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </>
  );
}

export function ArticleSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <View className="gap-4">
      <Text className="text-3xl font-semibold text-stone-950">{title}</Text>
      <View className="gap-4">{children}</View>
    </View>
  );
}

export function ArticleParagraph({ children }: { children: React.ReactNode }) {
  return <Text className="text-lg leading-8 text-stone-700">{children}</Text>;
}

export function ArticleBullets({ items }: { items: string[] }) {
  return (
    <View className="gap-3">
      {items.map((item) => (
        <View key={item} className="flex-row items-start gap-3">
          <View className="mt-2 h-2.5 w-2.5 rounded-full bg-emerald-600" />
          <Text className="flex-1 text-lg leading-8 text-stone-700">{item}</Text>
        </View>
      ))}
    </View>
  );
}

export function ArticleSources({ sources }: { sources: ArticleSource[] }) {
  return (
    <View className="gap-3">
      {sources.map((source) => (
        <Link key={source.href} href={source.href as Href} asChild>
          <Pressable
            accessibilityRole="link"
            className="flex-row items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white px-4 py-3"
          >
            <Text className="flex-1 text-base font-medium leading-6 text-emerald-700">
              {source.label}
            </Text>
            <Ionicons name="open-outline" size={18} color="#047857" />
          </Pressable>
        </Link>
      ))}
    </View>
  );
}

export function ArticleImage({
  source,
  alt,
  caption,
  variant = 'desktop',
}: {
  source: ImageSourcePropType;
  alt: string;
  caption: string;
  variant?: 'desktop' | 'phone';
}) {
  const isPhone = variant === 'phone';

  return (
    <View
      className="overflow-hidden rounded-[24px] border border-stone-200 bg-white shadow-sm"
      style={isPhone ? { alignSelf: 'center', maxWidth: 360, width: '100%' } : undefined}
    >
      <Image
        source={source}
        accessibilityLabel={alt}
        style={{ width: '100%', height: isPhone ? 580 : 360 }}
        resizeMode={isPhone ? 'contain' : 'cover'}
      />
      <View className="border-t border-stone-200 px-4 py-3">
        <Text className="text-sm leading-6 text-stone-500">{caption}</Text>
      </View>
    </View>
  );
}

export function QuickVerdict({
  title,
  items,
}: {
  title: string;
  items: string[];
}) {
  return (
    <View className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-5">
      <Text className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
        {title}
      </Text>
      <ArticleBullets items={items} />
    </View>
  );
}

function ArticleDisclaimer() {
  return (
    <View className="mx-auto mt-10 w-full max-w-3xl rounded-[24px] border border-stone-200 bg-white p-5">
      <Text className="text-sm leading-6 text-stone-500">
        Per Diem provides financial planning tools and simulations for informational
        and educational purposes only. It does not provide investment, tax, legal,
        or personalized financial advice. Projections and simulations are estimates,
        not guarantees. Screenshots shown here may use demo data.
      </Text>
    </View>
  );
}

function ArticleCtaBlock({
  title,
  description,
  onTryDemo,
  onSignup,
}: {
  title: string;
  description: string;
  onTryDemo: () => void;
  onSignup: () => void;
}) {
  return (
    <View className="mx-auto mt-10 w-full max-w-4xl rounded-[28px] border border-stone-200 bg-white p-6 shadow-sm">
      <Text className="text-2xl font-semibold text-stone-950">{title}</Text>
      <Text className="mt-3 text-base leading-7 text-stone-600">{description}</Text>
      <View className="mt-5 flex-row flex-wrap gap-3">
        <Pressable onPress={onTryDemo} className="rounded-2xl bg-emerald-600 px-5 py-4">
          <Text className="text-base font-semibold text-white">Try the demo profiles</Text>
        </Pressable>
        <Pressable
          onPress={onSignup}
          className="rounded-2xl border border-stone-300 px-5 py-4"
        >
          <Text className="text-base font-semibold text-stone-800">Create your account</Text>
        </Pressable>
      </View>
    </View>
  );
}
